import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Box, Button, Typography, IconButton, TextField } from '@mui/material';
import { CreditCard, Folder } from '@mui/icons-material';
import CustomTable from '@src/components/CustomTable';
import UserPackagesManagementApi from '@src/apis/userPackagesManagement';
import { INIT_PAGINATION } from '@src/constants';
import EditUserCreditsModal from './EditUserCreditsModal';
import UpdateUserPackageModal from './UpdateUserPackageModal';
import { StyledUserPackagesManagement } from './index.style';

const UserPackagesManagement = ({ appId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [userPackages, setUserPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(INIT_PAGINATION);

  const [searchTerm, setSearchTerm] = useState('');

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openUpdatePackageModal, setOpenUpdatePackageModal] = useState(false);
  const [selectedUserPackage, setSelectedUserPackage] = useState(null);

  const fetchUserPackages = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        ...(appId && { appId }),
        ...(searchTerm && { searchTerm }),
      };

      const response = await UserPackagesManagementApi.getUserPackages(params);

      if (response?.userPackages) {
        setUserPackages(response.userPackages || []);
        setPagination({
          ...pagination,
          page: response.pagination?.page || page,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0,
        });
      }
    } catch (error) {
      enqueueSnackbar(t('fetchUserPackagesError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchUserPackages(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (newPage) => {
    fetchUserPackages(newPage);
  };

  const handleEdit = (userPackage) => {
    setSelectedUserPackage(userPackage);
    setOpenEditModal(true);
  };

  const handleUpdatePackage = (userPackage) => {
    setSelectedUserPackage(userPackage);
    setOpenUpdatePackageModal(true);
  };

  const handleModalClose = () => {
    setOpenEditModal(false);
    setOpenUpdatePackageModal(false);
    setSelectedUserPackage(null);
  };

  const handleSuccess = () => {
    fetchUserPackages(pagination.page);
    handleModalClose();
  };

  useEffect(() => {
    fetchUserPackages();
  }, []);

  const heads = [
    {
      label: t('id'),
      valueName: 'id',
      align: 'left',
    },
    {
      label: t('userName'),
      valueName: 'userName',
      align: 'left',
    },
    {
      label: t('packageName'),
      valueName: 'packageName',
      align: 'left',
    },
    {
      label: t('remainingCredits'),
      valueName: 'remainingCredits',
      align: 'center',
    },
    {
      label: t('actions'),
      valueName: 'action',
      align: 'center',
    },
  ];

  const formatData = userPackages.map((userPackage) => ({
    ...userPackage,
    id: userPackage.id?.substring(0, 8) || '--',
    userName: userPackage.userId?.name || '--',
    packageName: userPackage.packageId?.name || '--',
    remainingCredits: userPackage.remainingCredits || 0,
    action: (
      <Box display="flex" gap={1} justifyContent="center">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(userPackage);
          }}
          title={t('editCredits')}
        >
          <CreditCard color="primary" fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleUpdatePackage(userPackage);
          }}
          title={t('updatePackage')}
        >
          <Folder color="secondary" fontSize="small" />
        </IconButton>
      </Box>
    ),
  }));

  return (
    <StyledUserPackagesManagement>
      <Box className="header-card">
        <Box className="header-card-title">
          <Typography variant="h3">{t('userPackagesManagement')}</Typography>
        </Box>

        <Box className="filters" display="flex">
          <Box display="flex" flex={1} gap={4}>
            <TextField
              className="search-input"
              size="small"
              placeholder={t('searchUserPackages')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
            />
          </Box>
          <Button variant="outlined" onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </Box>

      <Box className="body-card">
        <CustomTable
          actions={[]}
          items={formatData}
          heads={heads}
          loading={loading}
          pagination={pagination}
          onChangePagination={handlePageChange}
          disableRowClick
        />
      </Box>

      <EditUserCreditsModal
        open={openEditModal}
        userPackage={selectedUserPackage}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
      />

      <UpdateUserPackageModal
        open={openUpdatePackageModal}
        userPackage={selectedUserPackage}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
      />
    </StyledUserPackagesManagement>
  );
};

export default UserPackagesManagement;
